import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { AppConfig } from '@config';
import { getFromObject } from '@endurancewarranty/lib';
import { usePreviousValue } from '@endurancewarranty/ui';
import { AgentSessionEventEnum, AgentStatusEnum, SoftphoneConnectionBehaviorEnum, SoftphoneIntegrationEnum } from '@lib/enums';
import { impersonationAtom } from '@states/impersonation/impersonationAtom';
import { useJotaiAtom, useJotaiValue } from '@states/jotai/hooks';
import { currentCallAtom } from '@states/softphone/currentCallAtom';
import { INITIAL_SOFTPHONE_STATE, softphoneAtom } from '@states/softphone/softphoneAtom';
import { useSoftphoneWorker } from '@states/softphone/useSoftphoneWorker';
import type { FC, ReactNode } from 'react';
const ACTIVE_CALL_STATUS = [AgentStatusEnum.Acw, AgentStatusEnum.InboundContact, AgentStatusEnum.OutboundContact];
type Props = Readonly<{
  children: ReactNode;
}>;
export const ContactSession: FC<Props> = t0 => {
  const $ = _c(17);
  const {
    children
  } = t0;
  const {
    lastRoutedContactId,
    lastTouchedQuoteId
  } = useJotaiValue(currentCallAtom);
  const [softphoneState, setSoftphoneState] = useJotaiAtom(softphoneAtom);
  const {
    connectionBehavior
  } = useJotaiValue(softphoneAtom);
  const {
    previousValue: previousAgentStatus
  } = usePreviousValue(softphoneState.agentStatus);
  const {
    azureAdId
  } = useJotaiValue(impersonationAtom);
  const [dispositionQuoteId, setDispositionQuoteId] = useState(null);
  const isSoftphoneEnabled = azureAdId == null && AppConfig.softphoneIntegration.client === SoftphoneIntegrationEnum.Contacts;
  let t1;
  if ($[0] !== isSoftphoneEnabled || $[1] !== softphoneState.agentStatus) {
    t1 = {
      agentStatus: softphoneState.agentStatus,
      isEnabled: isSoftphoneEnabled
    };
    $[0] = isSoftphoneEnabled;
    $[1] = softphoneState.agentStatus;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    session
  } = useSoftphoneWorker(t1);
  let t2;
  if ($[3] !== session || $[4] !== setSoftphoneState) {
    t2 = () => {
      if (session == null) {
        return;
      }
      const events = session.events.sort(_temp);
      const latestDisconnectEvent = events?.findLast(_temp2);
      if (latestDisconnectEvent != null && latestDisconnectEvent.eventType === AgentSessionEventEnum.Disconnected) {
        setSoftphoneState({
          ...INITIAL_SOFTPHONE_STATE,
          connectionBehavior: SoftphoneConnectionBehaviorEnum.OptIn,
          agentStatus: AgentStatusEnum.Disconnected
        });
        return;
      }
      const latestStateEvent = events?.findLast(_temp3);
      if (latestStateEvent != null && latestStateEvent.eventType === AgentSessionEventEnum.StateEvent) {
        const agentStatus = getFromObject(AgentStatusEnum, latestStateEvent.currentState) ?? AgentStatusEnum.Unavailable;
        const newAgentStatus = latestStateEvent.isAcw === true ? AgentStatusEnum.Acw : agentStatus;
        setSoftphoneState(currentState => ({
          ...currentState,
          agentStatus: newAgentStatus
        }));
      }
      const latestCallEvent = events?.findLast(_temp4);
      if (latestCallEvent != null && latestCallEvent.eventType === AgentSessionEventEnum.CallContactEvent) {
        setSoftphoneState(currentState_0 => ({
          ...currentState_0,
          contactId: latestCallEvent.contactId,
          contactMasterId: latestCallEvent.contactMasterId,
          contactPhoneNumber: latestCallEvent.contactPhoneNumber,
          channel: latestCallEvent.channel,
          callDirection: latestCallEvent.callDirection,
          leadId: latestCallEvent.leadId,
          isTransfer: latestCallEvent.isTransfer,
          transferConsumerId: latestCallEvent.transferConsumerId,
          transferQuoteId: latestCallEvent.transferQuoteId,
          transferContractId: latestCallEvent.transferContractId,
          requestedOdometer: latestCallEvent.requestedOdometer,
          isVoicemail: latestCallEvent.isVoiceMail,
          isDialerReturn: latestCallEvent.isDialerReturn,
          isInternetResponder: latestCallEvent.isInternetResponder,
          isTvResponder: latestCallEvent.isTvResponder,
          isSpanishSkillActive: latestCallEvent.isSpanishSkillActive,
          sessionId: session.sessionId,
          isSimulatedCall: false
        }));
      }
    };
    $[3] = session;
    $[4] = setSoftphoneState;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== connectionBehavior || $[7] !== lastRoutedContactId || $[8] !== session || $[9] !== setSoftphoneState) {
    t3 = [connectionBehavior, lastRoutedContactId, session, setSoftphoneState];
    $[6] = connectionBehavior;
    $[7] = lastRoutedContactId;
    $[8] = session;
    $[9] = setSoftphoneState;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[11] !== dispositionQuoteId || $[12] !== lastTouchedQuoteId || $[13] !== previousAgentStatus || $[14] !== softphoneState.agentStatus) {
    t4 = () => {
      const isPreviousStatusOnCall = previousAgentStatus != null && ACTIVE_CALL_STATUS.includes(previousAgentStatus);
      const isCurrentStatusOffCall = !ACTIVE_CALL_STATUS.includes(softphoneState.agentStatus);
      const isCallEnded = isPreviousStatusOnCall && isCurrentStatusOffCall;
      if (isCallEnded && dispositionQuoteId == null) {
        setDispositionQuoteId(lastTouchedQuoteId);
      }
    };
    t5 = [dispositionQuoteId, previousAgentStatus, lastTouchedQuoteId, softphoneState.agentStatus];
    $[11] = dispositionQuoteId;
    $[12] = lastTouchedQuoteId;
    $[13] = previousAgentStatus;
    $[14] = softphoneState.agentStatus;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  return children;
};
function _temp(a, b) {
  return a.eventDateTimeUtc != null && b.eventDateTimeUtc != null ? new Date(a.eventDateTimeUtc).valueOf() - new Date(b.eventDateTimeUtc).valueOf() : 0;
}
function _temp2(event) {
  return event.eventType === AgentSessionEventEnum.Disconnected;
}
function _temp3(event_0) {
  return event_0.eventType === AgentSessionEventEnum.StateEvent;
}
function _temp4(event_1) {
  return event_1.eventType === AgentSessionEventEnum.CallContactEvent;
}